import { Popover, type PopoverProps } from "antd";
import classNames from "classnames";
import { type MouseEvent, type ReactNode, useCallback, useState } from "react";
import { Trans } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { IconLoading, IconMore } from "@icons/index";

export interface ClipMoreMenuOption {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

export interface ClipMoreMenuProps {
  options: ClipMoreMenuOption[];
  children?: ReactNode;
  className?: string;
  triggerClassName?: string;
  placement?: PopoverProps["placement"];
  overlayClassName?: string;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

const renderLabel = (label: ReactNode) => (typeof label === "string" ? <Trans>{label}</Trans> : label);

const renderPrefix = (option: ClipMoreMenuOption) => {
  if (option.loading) return <IconLoading className="loading h-[16px] w-[16px] animate-spin" />;
  return option.icon ?? null;
};

const hasPrefixSlot = (option: ClipMoreMenuOption) => Boolean(option.loading) || option.icon !== undefined;

const ClipMoreMenuItem = ({
  option,
  onItemClick,
}: {
  option: ClipMoreMenuOption;
  onItemClick: (option: ClipMoreMenuOption, e: MouseEvent<HTMLDivElement>) => void;
}) => {
  return (
    <div
      role="menuitem"
      className={twMerge(
        "flex h-[32px] w-full min-w-[104px] cursor-pointer items-center gap-[6px] rounded-[8px] px-[8px] py-[6px]",
        "text-[14px] leading-[20px] text-kds-fg transition-colors hover:bg-kds-bg-overlay",
        option.danger && "text-kds-danger-fg",
        option.disabled && "cursor-not-allowed opacity-50 hover:bg-transparent",
        option.loading && "pointer-events-none opacity-70",
        option.className,
      )}
      onClick={(e) => onItemClick(option, e)}
    >
      {hasPrefixSlot(option) && (
        <span className="flex size-[20px] shrink-0 items-center justify-center text-inherit">
          {renderPrefix(option)}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{renderLabel(option.label)}</span>
    </div>
  );
};

export const ClipMoreMenu = ({
  options,
  children,
  className,
  triggerClassName,
  placement = "bottomLeft",
  overlayClassName,
  getPopupContainer,
}: ClipMoreMenuProps) => {
  const [open, setOpen] = useState(false);
  const hasChildren = children !== undefined;
  const showTrigger = options.length > 0;

  const handleItemClick = useCallback((option: ClipMoreMenuOption, e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (option.disabled || option.loading) return;
    option.onClick?.(e);
  }, []);

  const panel = (
    <div
      className={classNames(
        "flex w-max min-w-0 flex-col gap-[4px]",
        "rounded-[12px] border border-kds-border bg-kds-bg-card p-[6px]",
        "shadow-[0_0_4px_var(--kds-shadow),0_8px_16px_var(--kds-shadow),0_16px_32px_var(--kds-shadow-soft)]",
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {options.map((option) => (
        <ClipMoreMenuItem key={option.key} option={option} onItemClick={handleItemClick} />
      ))}
    </div>
  );

  const trigger = (
    <div
      className={classNames(
        "h-[24px] w-[24px] cursor-pointer items-center justify-center rounded-[4px]",
        "bg-kds-mask text-kds-light",
        hasChildren ? "absolute right-[12px] top-[12px] hidden group-hover/clipMore:flex" : "hidden group-hover:flex",
        hasChildren ? triggerClassName : className,
        { "flex!": open },
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <IconMore className="h-[16px] w-[16px]" />
    </div>
  );

  const popover = showTrigger ? (
    <Popover
      trigger="hover"
      open={open}
      onOpenChange={setOpen}
      placement={placement}
      arrow={false}
      content={panel}
      getPopupContainer={getPopupContainer}
      classNames={{ root: overlayClassName, container: "border-none!" }}
      styles={{ container: { padding: 0, background: "transparent", boxShadow: "none" } }}
    >
      {trigger}
    </Popover>
  ) : null;

  if (hasChildren) {
    return (
      <div className={classNames("relative group/clipMore", className)}>
        {children}
        {popover}
      </div>
    );
  }

  return popover;
};
